import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { MdClose } from "react-icons/md";
import tw from "tailwind-styled-components";
import { ActionSection, ModalHeader, SideCancelBtn, SubmitBtn } from "../Styles/InputStyles";
import { dueTypes, source } from "../../Config";
import { paymentModesType } from "../../helpers";
import { useSelector } from "react-redux";

const FilterModal = ({
    onApplyFilter,
    handleClose,
    currentFilter = "",
    handleClearFilters,
    staffList
}) => {
    const locations = useSelector((state) => state.LocationsReducer.locations);
    const [activeType, setActiveType] = useState('staff');
    // const filteredLocations = locations.filter(location => location?.id !== 0);
    const filteredLocations = locations?.filter(location => location?.id !== 0) || [];
    const [selectedFilters, setSelectedFilters] = useState({
        staff: [],
        source: [],
        locations: []
    });
    const [height, setHeight] = useState(window.innerHeight);

    const getFilterGroups = () => {
        const groups = [

            {
                key: "staff",
                title: "By Staff",
                options: staffList.map(item => ({
                    label: item.name,
                    value: String(item.id),
                })),
                multiSelect: true
            },
            {
                key: "source",
                title: "By Source",
                options: source.map(item => ({
                    label: item.label,
                    value: String(item.value),
                })),
                multiSelect: true
            },

            {
                key: "locations",
                title: "Locations",
                options: filteredLocations.map(item => ({
                    label: item.name,
                    value: String(item.id),
                })),
                multiSelect: false
            },
        ];

        return groups;
    };

    const filterGroups = getFilterGroups();

    /* ------------------ preselect on open ------------------ */
    useEffect(() => {
        if (!currentFilter) return;

        if (typeof currentFilter === 'string') {
            for (const group of filterGroups) {
                const match = group.options.find(o => o.value === currentFilter);
                if (match) {
                    setActiveType(group.key);
                    setSelectedFilters(prev => ({
                        ...prev,
                        [group.key]: [{
                            value: match.value,
                            label: match.label,
                            type: group.title
                        }]
                    }));
                    break;
                }
            }
        }
        else if (typeof currentFilter === 'object') {
            const newSelectedFilters = {};
            filterGroups.forEach(group => {
                newSelectedFilters[group.key] = [];
            });

            Object.entries(currentFilter).forEach(([key, value]) => {
                if (Array.isArray(value)) {
                    const group = filterGroups.find(g => g.key === key);
                    if (group) {
                        if ((key === 'locations') && value.length > 0) {
                            newSelectedFilters[key] = [{
                                value: value[0].value,
                                label: value[0].label,
                                type: group.title
                            }];
                        } else {
                            newSelectedFilters[key] = value.map(item => ({
                                value: item.value,
                                label: item.label,
                                type: group.title
                            }));
                        }
                    }
                }
            });

            setSelectedFilters(newSelectedFilters);
            const firstActiveGroup = Object.entries(newSelectedFilters).find(([_, values]) => values.length > 0);
            setActiveType(firstActiveGroup ? firstActiveGroup[0] : filterGroups[0].key);
        }
    }, [currentFilter]);

    useEffect(() => {
        const resize = () => setHeight(window.innerHeight);
        window.addEventListener("resize", resize);
        return () => window.removeEventListener("resize", resize);
    }, []);

    const handleApply = () => {
        const hasSelectedFilters = Object.values(selectedFilters).some(filters => filters.length > 0);

        if (!hasSelectedFilters) {
            toast.error("Please select at least one filter before applying.");
            return;
        }
        onApplyFilter(selectedFilters);
    };

    const handleClearAllFilters = () => {
        setSelectedFilters({
            staff: [],
            source: [],
            locations: []
        });
        if (handleClearFilters) {
            handleClearFilters();
        }
    };

    const toggleFilter = (groupKey, option) => {
        setSelectedFilters(prev => {
            const currentGroupFilters = prev[groupKey];
            const group = filterGroups.find(g => g.key === groupKey);

            if (groupKey === 'locations') {

                if (currentGroupFilters.some(f => f.value === option.value)) {
                    return {
                        ...prev,
                        [groupKey]: []
                    };
                } else {

                    return {
                        ...prev,
                        [groupKey]: [{
                            value: option.value,
                            label: option.label,
                            type: group.title
                        }]
                    };
                }
            }

            const isSelected = currentGroupFilters.some(f => f.value === option.value);

            if (isSelected) {
                return {
                    ...prev,
                    [groupKey]: currentGroupFilters.filter(f => f.value !== option.value)
                };
            } else {
                return {
                    ...prev,
                    [groupKey]: [
                        ...currentGroupFilters,
                        {
                            value: option.value,
                            label: option.label,
                            type: group.title
                        }
                    ]
                };
            }
        });
    };

    const isFilterSelected = (groupKey, optionValue) => {
        return selectedFilters[groupKey]?.some(f => f.value === optionValue) || false;
    };

    const activeGroup = filterGroups.find(g => g.key === activeType) || filterGroups[0];

    return (
        <Container style={{ height }}>
            {/* Header */}
            <ModalHeader>
                <h1 className="text-lg font-medium text-white">Filters</h1>
                <button onClick={handleClose}>
                    <MdClose color="white" size={24} />
                </button>
            </ModalHeader>

            {/* Body */}
            <Body>
                {/* LEFT – Filter Types */}
                <LeftPane>
                    {filterGroups.map(group => {
                        const active = activeType === group.key;

                        return (
                            <TypeItem
                                key={group.key}
                                active={active}
                                onClick={() => setActiveType(group.key)}
                            >
                                <div className="flex justify-between items-center w-full">
                                    <span>{group.title}</span>

                                </div>
                            </TypeItem>
                        );
                    })}
                </LeftPane>

                {/* RIGHT – Options */}
                <RightPane>
                    <div className="flex justify-between items-center mb-4">
                        <FilterTitle>
                            {activeGroup.title}
                        </FilterTitle>
                    </div>

                    {activeGroup.options.map(option => {
                        const checked = isFilterSelected(activeGroup.key, option.value);

                        if (activeGroup.key === 'locations') {
                            return (
                                <OptionRadioRow
                                    key={option.value}
                                    onClick={() => toggleFilter(activeGroup.key, option)}
                                >
                                    <input
                                        type="radio"
                                        checked={checked}
                                        readOnly
                                        style={{ margin: 0, width: 16 }}
                                    />
                                    <span className="leading-tight text-sm text-gray-600">{option.label}</span>
                                </OptionRadioRow>
                            );
                        }

                        return (
                            <OptionRow
                                key={option.value}
                                onClick={() => toggleFilter(activeGroup.key, option)}
                            >
                                <div className="flex items-center gap-3">
                                    <Checkbox checked={checked}>
                                        {checked && (
                                            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                            </svg>
                                        )}
                                    </Checkbox>
                                    <span className={`leading-tight text-sm ${checked ? 'text-gray-800 font-medium' : 'text-gray-600'}`}>
                                        {option.label}
                                    </span>
                                </div>
                            </OptionRow>
                        );
                    })}
                </RightPane>
            </Body>

            <ActionSection>
                <SideCancelBtn type="button" onClick={handleClearAllFilters}>
                    Clear All
                </SideCancelBtn>
                <SubmitBtn type="submit" onClick={handleApply}>
                    Apply Filters
                </SubmitBtn>
            </ActionSection>
        </Container>
    );
};

const Container = tw.div`bg-white w-full h-full flex flex-col`;
const Header = tw.div`flex items-center justify-between bg-primary-color px-4 py-4`;
const Body = tw.div`flex flex-1 overflow-hidden`;
const LeftPane = tw.div`w-1/3 bg-gray-50 border-r overflow-y-auto`;
const TypeItem = tw.div`px-4 py-4 cursor-pointer text-md ${({ active }) => active ? "bg-blue-50 text-primary-color font-medium border-l-4 border-primary-color" : "text-gray-700 hover:bg-gray-100"}`;
const RightPane = tw.div`flex-1 py-4 pl-4 pr-6 overflow-y-auto mb-16`;
const OptionRow = tw.div`flex items-center justify-between py-3 px-2 cursor-pointer rounded-md hover:bg-gray-50 transition-colors duration-150`;
const FilterTitle = tw.h3`text-md font-medium text-gray-700`;
const Checkbox = tw.div`
  w-4 h-4 border-2 rounded flex items-center justify-center
  transition-colors duration-150
  ${({ checked }) => checked
        ? 'bg-primary-color border-primary-color'
        : 'border-gray-300 bg-white hover:border-gray-400'}
`;
const OptionRadioRow = tw.div`
  flex items-center gap-3
  py-3 px-2
  cursor-pointer
  text-sm text-gray-400
  rounded-md
  hover:bg-gray-50
`;
export default FilterModal;
